import {Button, Lead, Slot, Text, UnstableCard as Paper} from '@yandex-delivery/blocks'
import {Image} from '@yandex-delivery/blocks/image'
import {useI18n} from '@yandex-delivery/core/i18n'
import {addDate, flow, setDate} from '@yandex-delivery/utils'
import React, {useCallback, useMemo, useState} from 'react'

import {Drawer} from '../../components/Drawer'
import {tagToUrl} from '../../lib/tagToUrl'
import type {ActionPropsByType, ActionType} from '../types'
import type {DateTimeSelections, DateTimeState} from './components/DateTimePicker'
import {DateTimePicker} from './components/DateTimePicker'
import {getHoursInRange} from './utils/get-hours-in-range'
import {getMinutesInRange} from './utils/get-minutes-in-range'

export const SelectDueAction = ({
  onChange,
  isActive,
  setActive,
  ...props
}: ActionPropsByType<ActionType.SELECT_DUE>): React.ReactElement | null => {
  const i18n = useI18n()

  const dates = useMemo(() => props.slots.map(option => option.title), [props.slots])
  const [state, setState] = useState<DateTimeState>(() => {
    const defaultOptions = props.slots[0].options
    const defaultHour = getHoursInRange(defaultOptions.from, defaultOptions.to)[0]

    return {
      date: dates[0],
      hour: defaultHour,
      minute: getMinutesInRange(
        defaultOptions.from,
        defaultOptions.to,
        Number(defaultHour),
        defaultOptions.step_minutes,
      )[0],
    }
  })

  const selections = useMemo(
    () =>
      props.slots.reduce<DateTimeSelections>((acc, curr) => {
        acc[curr.title] = {
          date: dates,
          hour: getHoursInRange(curr.options.from, curr.options.to),
          minute: getMinutesInRange(curr.options.from, curr.options.to, Number(state.hour), curr.options.step_minutes),
        }

        return acc
      }, {}),
    [props.slots, dates, state.hour],
  )

  const handleClose = useCallback(() => {
    setActive?.(false)
  }, [setActive])

  const handleSubmit = useCallback(() => {
    const getDate = flow(
      Date,
      setDate(0, 'hour'),
      setDate(0, 'minute'),
      setDate(0, 'second'),
      setDate(0, 'millisecond'),
      addDate(dates.indexOf(state.date), 'days'),
      addDate(Number(state.hour), 'hours'),
      addDate(Number(state.minute), 'minutes'),
      date => date.toISOString(),
    )

    onChange?.(getDate())
    handleClose()
  }, [dates, handleClose, onChange, state.date, state.hour, state.minute])

  return (
    <Drawer onClose={handleClose} opened={isActive} title={props.title}>
      <Paper padding="none">
        <DateTimePicker onChange={setState} selections={selections} value={state} />
      </Paper>
      <Paper padding="S">
        <Slot mb="s">
          <Lead>
            <Image height={24} src={tagToUrl(props.description.icon.image_tag)} />
          </Lead>
          <Text>
            {i18n('bdui.date.delivery_time', {
              placeholder: {
                date: state.date.toLowerCase(),
                time: `${state.hour}:${state.minute}`,
              },
            })}
          </Text>
        </Slot>
        <Button onPress={handleSubmit} variant="action">
          {i18n('bdui.date.submit_button.title', 'Продолжить')}
        </Button>
      </Paper>
    </Drawer>
  )
}

export const getSelectDueAction = (setActive: (value: boolean) => void) => () => {
  setActive(true)
}
